import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Flex, Text, TextField } from '@radix-ui/themes';
import { Button, FieldError, HeaderItalic, ServerErrorCallout } from '@/components/common';
import { GoogleIcon } from '@/components/icons';
import { PulpLogo, WizardHeader, WizardScreen } from '@/components/layout';
import { analyticsClient } from '@/lib';
import { authService } from '@/services';

type Translate = (key: string) => string;

const createLoginSchema = (t: Translate) =>
  z.object({
    email: z
      .string()
      .min(1, t('errors.emailRequired'))
      .check(z.email(t('errors.emailInvalid'))),
    password: z.string().min(1, t('errors.passwordRequired')),
  });

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>;

const LoginForm = ({
  loading,
  emailPlaceholder,
  emailLabel,
  passwordPlaceholder,
  passwordLabel,
  submitLabel,
  register,
  errors,
  onSubmit,
}: {
  loading: boolean;
  emailPlaceholder: string;
  emailLabel: string;
  passwordPlaceholder: string;
  passwordLabel: string;
  submitLabel: string;
  register: UseFormRegister<LoginFormValues>;
  errors: FieldErrors<LoginFormValues>;
  onSubmit: SubmitEventHandler<HTMLFormElement>;
}) => (
  <Box
    p="4"
    style={{
      background: 'rgb(0 0 0 / 0.45)',
      border: '0.5px solid var(--gray-a6)',
      borderRadius: 16,
    }}
  >
    <form onSubmit={onSubmit} noValidate>
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <TextField.Root
            type="email"
            placeholder={emailPlaceholder}
            aria-label={emailLabel}
            data-testid="login-email"
            aria-invalid={errors.email ? true : undefined}
            {...register('email')}
          />
          <FieldError message={errors.email?.message} />
        </Flex>

        <Flex direction="column" gap="1">
          <TextField.Root
            type="password"
            placeholder={passwordPlaceholder}
            aria-label={passwordLabel}
            data-testid="login-password"
            aria-invalid={errors.password ? true : undefined}
            {...register('password')}
          />
          <FieldError message={errors.password?.message} />
        </Flex>

        <Button type="submit" disabled={loading} fullWidth>
          {submitLabel}
        </Button>
      </Flex>
    </form>
  </Box>
);

const GoogleButton = ({
  loading,
  label,
  onGoogleSignIn,
}: {
  loading: boolean;
  label: string;
  onGoogleSignIn: () => void;
}) => (
  <Button
    type="button"
    onClick={onGoogleSignIn}
    disabled={loading}
    variant="outline"
    color="gray"
    size="md"
    fullWidth
  >
    <Flex align="center" gap="2" as="span">
      <GoogleIcon />
      {label}
    </Flex>
  </Button>
);

/** v90 s-login — returning user sign-in with logo. */
export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const supportEmail =
    (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? 'support@pulp.app';

  const schema = useMemo(() => createLoginSchema(key => t(key)), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const handleLogin = async (data: { email: string; password: string }) => {
    setError(null);
    setLoading(true);
    try {
      const user = await authService.loginWithEmailPassword(data);
      analyticsClient.capture('login_completed', { method: 'email_password' });
      navigate(user.emailVerified ? '/' : '/verify-email');
    } catch (err: unknown) {
      console.error(err);
      if (err instanceof Error && err.message === 'AUTH_BANNED') {
        setError(t('errors.banned', { email: supportEmail }));
      } else {
        setError(t('errors.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);
    try {
      const { user } = await authService.authWithGoogle('login');
      analyticsClient.capture('login_completed', { method: 'google' });
      navigate(user.emailVerified ? '/' : '/verify-email');
    } catch (err: unknown) {
      console.error('Google Login Failure:', err);
      if (err instanceof Error) {
        if (err.message === 'AUTH_REQUIRES_INVITE_CODE') {
          toast.error(t('errors.googleAccountNotRegistered'));
          setTimeout(() => navigate('/birthday'), 2500);
        } else if (err.message === 'AUTH_BANNED') {
          setError(t('errors.banned', { email: supportEmail }));
        } else if (err.message === 'auth/popup-blocked') {
          setError(t('errors.popupBlocked'));
        } else {
          setError(err.message);
        }
      } else {
        setError(t('errors.googleSignInFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <WizardScreen header={<WizardHeader onBack={() => navigate('/')} />}>
      <Flex direction="column" gap="4" height="100%" pt="2">
        <Flex justify="center" mt="2">
          <PulpLogo />
        </Flex>

        <HeaderItalic align="center">{t('login.headline')}</HeaderItalic>

        {error ? <ServerErrorCallout message={error} /> : null}

        <LoginForm
          loading={loading}
          emailPlaceholder={t('form.email.placeholder')}
          emailLabel={t('form.email.label')}
          passwordPlaceholder={t('form.password.placeholder')}
          passwordLabel={t('form.password.label')}
          submitLabel={t('login.submit')}
          register={register}
          errors={errors}
          onSubmit={handleSubmit(handleLogin)}
        />

        <Button
          type="button"
          onClick={() => navigate('/forgot-password')}
          variant="ghost"
          color="gray"
        >
          {t('actions.forgotPassword')}
        </Button>

        <Text size="1" color="gray" align="center">
          {t('login.divider')}
        </Text>

        <GoogleButton
          loading={loading}
          label={t('actions.google')}
          onGoogleSignIn={() => void handleGoogleLogin()}
        />

        <Button
          type="button"
          onClick={() => navigate('/birthday')}
          variant="ghost"
          color="gray"
          fullWidth
        >
          {t('actions.switchToRegister')}
        </Button>
      </Flex>
    </WizardScreen>
  );
}
